import React, { useEffect, useState } from 'react';
import Modal from '../../components/Modal';
import SearchSelect from '../../components/SearchSelect';
import { GroupInfo, RoleInfo, saveGroup } from '../../api/authz';
import { ApiError } from '../../api/request';

/**
 * 用户组编辑 (2026-09-13): 组名 (新建时填, 之后不可改) / 显示名 / 说明 / 整套角色.
 * 角色选择与用户权限面板同一交互: 已选标签可摘, 搜索下拉追加; 系统角色不列出 (后端也会拒绝).
 */
interface Props {
    open: boolean;
    /** 编辑对象; 缺省 = 新建 */
    source?: GroupInfo;
    roles: RoleInfo[];
    onClose: () => void;
    onSaved: () => void;
}

const NAME_PATTERN = /^[a-z][a-z0-9-]{1,62}[a-z0-9]$/;

function GroupEditorModal({ open, source, roles, onClose, onSaved }: Props) {
    const [name, setName] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [description, setDescription] = useState('');
    const [picked, setPicked] = useState<string[]>([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;
        setName(source?.name || '');
        setDisplayName(source?.displayName || '');
        setDescription(source?.description || '');
        setPicked(source?.roles || []);
        setError('');
    }, [open, source]);

    const nameOk = NAME_PATTERN.test(name.trim());

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!nameOk) {
            setError('组名须为 3~64 位小写字母 / 数字 / 连字符, 以字母开头');
            return;
        }
        setBusy(true);
        setError('');
        try {
            await saveGroup({
                name: name.trim(),
                displayName: displayName.trim() || name.trim(),
                description: description.trim(),
                roles: picked,
            });
            onSaved();
        } catch (err) {
            setError(
                err instanceof ApiError
                    ? `${err.code}: ${err.message}`
                    : String(err),
            );
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            open={open}
            title={source ? '编辑用户组' : '新建用户组'}
            subtitle={
                source ? (
                    <span className="font-mono">{source.name}</span>
                ) : (
                    '组内每个成员都将获得组上配置的全部角色'
                )
            }
            onClose={onClose}
            width={640}
        >
            <form onSubmit={onSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                        <span className="mb-1 block text-xs text-slate-500">
                            组名 (英文标识, 创建后不可改)
                        </span>
                        <input
                            className="inp w-full font-mono"
                            placeholder="material-team"
                            value={name}
                            disabled={!!source}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </label>
                    <label className="block">
                        <span className="mb-1 block text-xs text-slate-500">
                            显示名
                        </span>
                        <input
                            className="inp w-full"
                            placeholder="材料组"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                        />
                    </label>
                </div>
                <label className="block">
                    <span className="mb-1 block text-xs text-slate-500">
                        说明 (可选)
                    </span>
                    <input
                        className="inp w-full"
                        placeholder="例如: 材料主数据录入与维护同事"
                        value={description}
                        maxLength={255}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                </label>

                <div>
                    <div className="mb-1 text-xs text-slate-500">
                        组角色 —— 组内每个成员都会获得
                        <span className="ml-1 text-slate-400">
                            (管理员 / 普通用户两个系统角色不在列表里:
                            身份与准入只能对人授予)
                        </span>
                    </div>
                    <div className="rounded-lg border border-slate-200 p-3">
                        <div className="flex flex-wrap items-center gap-2">
                            {picked.length === 0 && (
                                <span className="text-xs text-slate-400">
                                    暂无角色 —— 在右侧搜索添加
                                </span>
                            )}
                            {picked.map((n) => {
                                const r = roles.find((x) => x.name === n);
                                return (
                                    <span
                                        key={n}
                                        className="inline-flex items-center gap-1.5 rounded-full border border-brand-200 bg-brand-50 pl-3 pr-1.5 py-1 text-sm text-brand-700"
                                        title={n}
                                    >
                                        {r?.displayName || n}
                                        <button
                                            type="button"
                                            className="flex h-5 w-5 items-center justify-center rounded-full text-brand-400 hover:bg-brand-100 hover:text-rose-600"
                                            aria-label={`移除 ${r?.displayName || n}`}
                                            onClick={() =>
                                                setPicked((p) =>
                                                    p.filter((x) => x !== n),
                                                )
                                            }
                                        >
                                            ×
                                        </button>
                                    </span>
                                );
                            })}
                            <SearchSelect
                                className="w-64"
                                value=""
                                placeholder="搜索并添加角色…"
                                options={roles
                                    .filter(
                                        (r) =>
                                            !r.system &&
                                            !picked.includes(r.name),
                                    )
                                    .map((r) => ({
                                        value: r.name,
                                        label: `${r.displayName} (${r.name})`,
                                    }))}
                                onChange={(v) => {
                                    if (v)
                                        setPicked((p) =>
                                            p.includes(v) ? p : [...p, v],
                                        );
                                }}
                            />
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="rounded border border-rose-200 bg-rose-50 p-2 text-sm text-rose-600">
                        {error}
                    </div>
                )}
                <div className="flex items-center justify-end gap-2">
                    <button
                        type="button"
                        className="btn-ghost"
                        onClick={onClose}
                    >
                        取消
                    </button>
                    <button
                        type="submit"
                        className="btn-primary"
                        disabled={busy || !nameOk}
                    >
                        {busy ? '保存中…' : '保存'}
                    </button>
                </div>
            </form>
        </Modal>
    );
}

export default GroupEditorModal;
